import { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsChevronDown, BsCheck } from 'react-icons/bs';

import { fieldClasses } from '#globalComponents/Forms/fieldStyles';
import FormSelect from '#globalComponents/Forms/Select';
import FormTextInput from '#globalComponents/Forms/TextInput';

// Text input

export const TextInput = ({ id, label, placeholder, value, onChange }) => (
	<div>
		{label && (
			<label htmlFor={id} className="block text-xs text-bgBase-800 mb-1">
				{label}
			</label>
		)}
		<FormTextInput
			id={id}
			placeholder={placeholder}
			value={value || ''}
			onChange={(e) => onChange(e.target.value)}
		/>
	</div>
);

TextInput.propTypes = {
	id: PropTypes.string,
	label: PropTypes.string,
	placeholder: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};

// Native select

export const Select = ({ id, label, options, value, onChange, placeholderOption, disabled = false }) => (
	<div>
		{label && (
			<label htmlFor={id} className="block text-xs text-bgBase-800 mb-1">
				{label}
			</label>
		)}
		<FormSelect id={id} value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
			{placeholderOption && <option value="">{placeholderOption}</option>}
			{options.map((o) => {
				const optValue = typeof o === 'string' ? o : o.value;
				const optLabel = typeof o === 'string' ? o : o.label;
				return (
					<option key={optValue} value={optValue}>
						{optLabel}
					</option>
				);
			})}
		</FormSelect>
	</div>
);

Select.propTypes = {
	id: PropTypes.string,
	label: PropTypes.string,
	options: PropTypes.array.isRequired,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	placeholderOption: PropTypes.string,
	disabled: PropTypes.bool,
};

// Native date input

export const DateInput = ({ id, label, value, onChange, min, max }) => (
	<div>
		{label && (
			<label htmlFor={id} className="block text-xs text-bgBase-800 mb-1">
				{label}
			</label>
		)}
		<FormTextInput
			type="date"
			id={id}
			value={value || ''}
			min={min}
			max={max}
			onChange={(e) => onChange(e.target.value)}
		/>
	</div>
);

DateInput.propTypes = {
	id: PropTypes.string,
	label: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	min: PropTypes.string,
	max: PropTypes.string,
};

// Min/Max range pair

export const RangeInput = ({
	fromValue,
	toValue,
	onFrom,
	onTo,
	fromPlaceholder = 'Min',
	toPlaceholder = 'Max',
	unit,
	step = 1,
	min,
}) => (
	<div className="flex items-center gap-2">
		<FormTextInput
			type="number"
			value={fromValue ?? ''}
			step={step}
			min={min}
			placeholder={fromPlaceholder}
			onChange={(e) => onFrom(e.target.value)}
		/>
		<span className="text-bgBase-800 text-xs">–</span>
		<FormTextInput
			type="number"
			value={toValue ?? ''}
			step={step}
			min={min}
			placeholder={toPlaceholder}
			onChange={(e) => onTo(e.target.value)}
		/>
		{unit && <span className="text-[11px] text-bgBase-800 whitespace-nowrap">{unit}</span>}
	</div>
);

RangeInput.propTypes = {
	fromValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	toValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onFrom: PropTypes.func.isRequired,
	onTo: PropTypes.func.isRequired,
	fromPlaceholder: PropTypes.string,
	toPlaceholder: PropTypes.string,
	unit: PropTypes.string,
	step: PropTypes.number,
	min: PropTypes.number,
};

// Slider + numeric input (lead count)

export const RangeSliderWithInput = ({ min, max, value, onChange, label }) => (
	<div>
		<div className="flex items-center justify-between gap-2 mb-1.5">
			<label className="text-xs text-bgBase-800">{label}</label>
			<FormTextInput
				type="number"
				min={min}
				max={max}
				value={value}
				onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value))))}
				className="!w-20 !px-2.5 text-right tabular-nums"
			/>
		</div>
		<input
			type="range"
			min={min}
			max={max}
			value={value}
			onChange={(e) => onChange(Number(e.target.value))}
			className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
		/>
		<div className="flex justify-between text-[10px] text-bgBase-800 mt-1">
			<span>{min}</span>
			<span>{max}</span>
		</div>
	</div>
);

RangeSliderWithInput.propTypes = {
	min: PropTypes.number.isRequired,
	max: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
	onChange: PropTypes.func.isRequired,
	label: PropTypes.string,
};

// Segmented two-way toggle (e.g. Ort/Bundesland vs. PLZ-Bereich)

export const SegmentedToggle = ({ options, value, onChange }) => (
	<div className="inline-flex p-0.5 bg-gray-100 rounded-md">
		{options.map((o) => (
			<button
				key={o.value}
				type="button"
				onClick={() => onChange(o.value)}
				className={`px-2.5 py-1 text-xs rounded transition-colors ${
					value === o.value
						? 'bg-white text-gray-900 font-medium shadow-sm'
						: 'text-gray-500 hover:text-gray-700'
				}`}
			>
				{o.label}
			</button>
		))}
	</div>
);

SegmentedToggle.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
		}),
	).isRequired,
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
};

// Multi-select dropdown (legal forms)

export const MultiSelect = ({ options, selected, onChange, placeholder }) => {
	const [open, setOpen] = useState(false);
	const ref = useRef(null);

	useEffect(() => {
		const handler = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false);
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, []);

	const toggle = (value) => onChange(value);

	// Escape closes the list only; the surrounding modal listens on document and must not
	// see the same key.
	const handleKeyDown = (e) => {
		if (e.key !== 'Escape' || !open) return;
		e.stopPropagation();
		setOpen(false);
	};

	return (
		<div className="relative" ref={ref} onKeyDown={handleKeyDown}>
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className={fieldClasses(
					'h-10 rounded-card px-4 flex items-center justify-between gap-2 text-left',
					false,
				)}
			>
				<span className={selected.length ? 'text-text truncate' : 'text-bgBase-800'}>
					{selected.length ? `${selected.length} ausgewählt` : placeholder}
				</span>
				<BsChevronDown
					className={`w-3.5 h-3.5 text-bgBase-700 transition-transform ${open ? 'rotate-180' : ''}`}
				/>
			</button>
			{open && (
				<div className="absolute z-40 w-full mt-1 bg-white border border-bgBase-500 rounded-card shadow-elevated max-h-52 overflow-y-auto py-1">
					{options.map((o) => {
						const optValue = typeof o === 'string' ? o : o.value;
						const optLabel = typeof o === 'string' ? o : o.label;
						const isSelected = selected.includes(optValue);
						return (
							<button
								key={optValue}
								type="button"
								onClick={() => toggle(optValue)}
								className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-gray-50 ${isSelected ? 'text-primary-700 font-medium' : 'text-text'}`}
							>
								<span
									className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}`}
								>
									{isSelected && <BsCheck className="w-2.5 h-2.5 text-white" />}
								</span>
								{optLabel}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

MultiSelect.propTypes = {
	options: PropTypes.array.isRequired,
	selected: PropTypes.arrayOf(PropTypes.string).isRequired,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
};
